import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearAuth, getUser } from '../lib/auth';
import { subscriptionAPI, authAPI } from '../lib/api';
import { useAuth } from '../App';
import { useTranslation } from '../lib/i18n';
import { Button } from './ui/button';
import RCLogo, { RCLogoMark } from './Logo';
import Footer from './Footer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Home, LogOut, Video, User, Building2, Crown, Zap, Globe, Instagram, CreditCard, ChevronDown, Database, Menu, X, Users, Sparkles
} from 'lucide-react';

// Nav items, partitioned by tier-gate. Free users only see the slim
// set on the left; everything Pro-only is hidden until they upgrade,
// per the "no confusion / one obvious path" v1.5 onboarding goal.
// Compose the effective lists inline (see below) so a tier change
// live-updates without a remount.
const PRIMARY_NAV_FREE = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: Home },
  { path: '/properties', labelKey: 'nav.properties', icon: Building2 },
  // Videos elevated to primary nav. They're the core output of the app,
  // they need to be one click away. Previously buried in the secondary
  // dropdown which made discovery harder than it should be.
  { path: '/gallery', labelKey: 'nav.videos', icon: Video },
  // Teaser until launch: the page explains what is coming and collects
  // early-tester sign-ups. Drop `soon` once the studio ships.
  { path: '/studio', labelKey: 'nav.studio', icon: Sparkles, soon: true },
];
const PRIMARY_NAV_PRO_EXTRAS = [
  { path: '/auto-post', labelKey: 'nav.autoPost', icon: Zap },
];

const SECONDARY_NAV_FREE = [
  { path: '/profile', labelKey: 'nav.profile', icon: User },
  { path: '/pricing', labelKey: 'nav.subscription', icon: CreditCard },
];
const SECONDARY_NAV_PRO_EXTRAS = [
  { path: '/social', labelKey: 'nav.social', icon: Instagram },
];
// Imports are available from Starter upward (v1.8.1) — split out of the
// pro extras so the €29 tier sees its listing sources without also
// getting the socials entry it can't use.
// /imports replaced /justimmo: the single hub for both Justimmo +
// Willhaben source connections. Justimmo's own page is still reachable
// from inside /imports via its "Verwalten" button.
const SECONDARY_NAV_STARTER_EXTRAS = [
  { path: '/imports', labelKey: 'nav.imports', icon: Database },
];

const TEAM_NAV_ITEM = { path: '/team', labelKey: 'nav.team', icon: Users };

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, setLanguage } = useTranslation();
  const { markAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes. Otherwise it sticks
  // open after a navigation tap, which feels broken.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await subscriptionAPI.getStatus();
      setSubscription(response.data);
    } catch (error) {
      // Silent: only drives a sidebar badge. 401 redirects via interceptor.
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Ignore logout API errors
    }
    clearAuth();
    // Clear context state so a subsequent login on the same tab sees a
    // clean slate instead of inheriting stale `authenticated = true`.
    markAuthenticated(false);
    navigate('/auth');
    toast.success(t('auth.logoutSuccess'));
  };

  const toggleLanguage = () => {
    setLanguage(lang === 'de' ? 'en' : 'de');
  };

  const isActive = (path) => location.pathname === path;
  // Three states matter, not two:
  //   - subscription === null   → still loading; render neutral UI, no
  //                               "Free Plan" banner flicker for paying users.
  //   - tier === 'pro'/'solo'   → paid; hide free-tier nudges entirely.
  //   - tier === 'free'         → free; show usage banner + upgrade CTA.
  // Earlier code collapsed the "loading" state into "free" via a
  // silent ?? cast, which made paying users briefly see the upgrade
  // prompt on every page load. The free-tier banner below now also
  // gates on `subscription` being truthy (not just `!isPro`).
  const subscriptionLoaded = subscription !== null;
  const tier = subscription?.subscription_tier;
  const isPro = tier === 'pro' || tier === 'solo' || tier === 'team';
  // Compose nav lists from the free/pro partitions defined above.
  // Free users see fewer items so the "render your first video" path
  // is the dominant one. No Auto-Post / Social / Justimmo distractions
  // until they upgrade. Team-tier users get the Team nav item too.
  const effectivePrimaryNav = isPro
    ? [...PRIMARY_NAV_FREE.slice(0, 1), ...PRIMARY_NAV_PRO_EXTRAS, ...PRIMARY_NAV_FREE.slice(1)]
    : PRIMARY_NAV_FREE;
  const effectiveSecondaryNav = (() => {
    const isStarterPlus = isPro || tier === 'starter';
    const extras = [
      ...(isPro ? SECONDARY_NAV_PRO_EXTRAS : []),
      ...(isStarterPlus ? SECONDARY_NAV_STARTER_EXTRAS : []),
    ];
    let nav = extras.length
      ? [...SECONDARY_NAV_FREE.slice(0, 2), ...extras, ...SECONDARY_NAV_FREE.slice(2)]
      : SECONDARY_NAV_FREE;
    if (tier === 'team') nav = [TEAM_NAV_ITEM, ...nav];
    return nav;
  })();

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 " />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#dfe8db] rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative border-b border-[#e3dccb] backdrop-blur-xl bg-[#f7f3ea]/85 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo. Full lockup (mark + wordmark). The Logo component
              renders a real SVG of the brand mark; the wordmark uses
              the brand violet→teal gradient on "Real" with a glowing
              teal dot separator. */}
          <div
            className="flex items-center cursor-pointer"
            onClick={() => navigate('/dashboard')}
            data-testid="nav-logo"
            role="button"
            aria-label={t('nav.logoToDashboard') || 'Real Content. Zur Übersicht'}
          >
            <RCLogo size="md" />
          </div>

          {/* Mobile hamburger. Primary nav, lang, subscription all live in
              the drawer so the small-screen header stays clean. */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden p-2 rounded-md text-[#4a463b] hover:text-[#23211a] hover:bg-[#efe9db] transition"
            aria-label={t('nav.openMenu') || 'Menü öffnen'}
            data-testid="nav-mobile-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Desktop navigation (md+) */}
          <nav className="hidden md:flex items-center gap-1">
            {/* Primary nav items */}
            {effectivePrimaryNav.map((item) => (
              <Button
                key={item.path}
                data-testid={`nav-${item.labelKey.split('.')[1]}`}
                variant="ghost"
                onClick={() => navigate(item.path)}
                title={t(item.labelKey)}
                aria-label={t(item.labelKey)}
                className={`relative text-sm ${
                  isActive(item.path)
                    ? 'text-[#1f4d3a] bg-[#e9efe7]'
                    : 'text-[#6f6a5c] hover:text-[#23211a]'
                }`}
              >
                {/* Labels only from xl: below that the full row (logo +
                    up to 5 items + language + plan badge + avatar) does
                    not fit and the header overflowed. */}
                <item.icon className="w-4 h-4 xl:mr-2" />
                <span className="hidden xl:inline">{t(item.labelKey)}</span>
                {item.soon && (
                  <>
                    <span className="xl:hidden absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-[#c7a869]" aria-hidden="true" />
                    <span className="ml-1.5 hidden xl:inline rounded-full bg-[#f6edd8] border border-[#c7a869] px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-[#8f6f3a]">
                      {t('nav.soon')}
                    </span>
                  </>
                )}
              </Button>
            ))}

            {/* Language toggle */}
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="text-[#6f6a5c] hover:text-[#23211a] ml-1"
              data-testid="lang-toggle"
              title={t('nav.language')}
            >
              <Globe className="w-4 h-4 mr-1" />
              <span className="text-xs uppercase font-medium">{lang}</span>
            </Button>

            {/* Subscription Badge / Upgrade Button. Gated on
                subscriptionLoaded so paying users don't see "Upgrade"
                flash for the few hundred ms before the API resolves. */}
            {!subscriptionLoaded ? null : isPro ? (
              <div className="flex items-center gap-1 px-3 py-1.5 bg-[#f6edd8] border border-[#c7a869] rounded-full ml-1">
                <Crown className="w-4 h-4 text-[#8f6f3a]" />
                <span className="text-xs font-medium text-[#8f6f3a]">Autopilot</span>
              </div>
            ) : (
              <Button
                data-testid="nav-upgrade"
                variant="ghost"
                onClick={() => navigate('/pricing')}
                className="text-sm text-[#1f4d3a] hover:text-[#1f4d3a] hover:bg-[#e9efe7] ml-1"
              >
                <Zap className="w-4 h-4 mr-1" />
                {t('common.upgrade')}
              </Button>
            )}

            {/* Avatar dropdown menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-[#6f6a5c] hover:text-[#23211a] ml-1"
                  data-testid="nav-avatar-menu"
                >
                  <User className="w-4 h-4 mr-1" />
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48 bg-[#fffdf7] border-[#d8d0bd]"
              >
                <DropdownMenuItem
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer text-[#4a463b] hover:text-[#23211a] focus:text-[#23211a] focus:bg-[#e9efe7]"
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('nav.profile')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/social')}
                  className="cursor-pointer text-[#4a463b] hover:text-[#23211a] focus:text-[#23211a] focus:bg-[#e9efe7]"
                >
                  <Instagram className="w-4 h-4 mr-2" />
                  {t('nav.social')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/gallery')}
                  className="cursor-pointer text-[#4a463b] hover:text-[#23211a] focus:text-[#23211a] focus:bg-[#e9efe7]"
                >
                  <Video className="w-4 h-4 mr-2" />
                  {t('nav.videos')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/imports')}
                  className="cursor-pointer text-[#4a463b] hover:text-[#23211a] focus:text-[#23211a] focus:bg-[#e9efe7]"
                >
                  <Database className="w-4 h-4 mr-2" />
                  {t('nav.imports')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/pricing')}
                  className="cursor-pointer text-[#4a463b] hover:text-[#23211a] focus:text-[#23211a] focus:bg-[#e9efe7]"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {t('nav.subscription')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#d8d0bd]" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-[#b3543f] hover:text-[#a34430] focus:text-[#a34430] focus:bg-[#a34430]/20"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </header>

      {/* Mobile nav drawer */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer */}
          <div className="relative w-72 max-w-[85vw] bg-[#f7f3ea] border-r border-[#e3dccb] shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-4 border-b border-[#e3dccb]">
              <RCLogo size="sm" />
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="p-1.5 rounded-md text-[#6f6a5c] hover:text-[#23211a] hover:bg-[#efe9db]"
                aria-label={t('nav.closeMenu') || 'Menü schließen'}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Primary navigation. Bigger tap targets, no tooltips */}
            <div className="flex-1 overflow-y-auto py-2">
              <div className="px-3 pb-2 text-[11px] uppercase tracking-wider text-[#8a8474] font-medium">
                {t('nav.primary') || 'Hauptmenü'}
              </div>
              {effectivePrimaryNav.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition ${
                    isActive(item.path)
                      ? 'text-[#1f4d3a] bg-[#e9efe7] border-l-2 border-[#1f4d3a]'
                      : 'text-[#4a463b] hover:text-[#23211a] hover:bg-[#efe9db]/40 border-l-2 border-transparent'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {t(item.labelKey)}
                  {item.soon && (
                    <span className="ml-auto rounded-full bg-[#f6edd8] border border-[#c7a869] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#8f6f3a]">
                      {t('nav.soon')}
                    </span>
                  )}
                </button>
              ))}

              <div className="mt-4 px-3 pb-2 text-[11px] uppercase tracking-wider text-[#8a8474] font-medium">
                {t('nav.secondary') || 'Konto & Tools'}
              </div>
              {effectiveSecondaryNav.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition ${
                    isActive(item.path)
                      ? 'text-[#1f4d3a] bg-[#e9efe7] border-l-2 border-[#1f4d3a]'
                      : 'text-[#4a463b] hover:text-[#23211a] hover:bg-[#efe9db]/40 border-l-2 border-transparent'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {t(item.labelKey)}
                </button>
              ))}
            </div>

            {/* Footer actions */}
            <div className="border-t border-[#e3dccb] p-3 space-y-2">
              <button
                onClick={toggleLanguage}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-[#4a463b] hover:text-[#23211a] hover:bg-[#efe9db]/40 rounded-md transition"
              >
                <span className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {t('nav.language')}
                </span>
                <span className="text-xs uppercase font-mono text-[#6f6a5c]">{lang}</span>
              </button>
              {subscriptionLoaded && !isPro && (
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#1f4d3a] bg-[#e9efe7] hover:bg-[#dfe8db] rounded-md transition"
                >
                  <Zap className="w-4 h-4" />
                  {t('common.upgrade')}
                </button>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#a34430] hover:bg-[#f7e8e3] rounded-md transition"
              >
                <LogOut className="w-4 h-4" />
                {t('common.logout')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Free/Starter usage banner. Mobile stacks message + upgrade
          button vertically so the CTA never gets pushed off-screen
          when the videos-used string is long. Free shows the lifetime
          test-video counter; Starter shows this month's usage against
          its advertised 5/Monat cap. */}
      {subscription && !isPro && subscription.limits && (
        <div className="relative bg-[#e9efe7] border-b border-[#1f4d3a]/30">
          <div className="max-w-7xl mx-auto px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-sm text-[#4a463b]">
              <span className="font-medium">
                {tier === 'starter' ? (t('starterTier.label') || 'Starter') : t('freeTier.label')}:
              </span>{' '}
              {tier === 'starter'
                ? t('starterTier.videosUsed', {
                    count: subscription.videos_this_month ?? 0,
                    max: subscription.limits.max_videos_monthly,
                  })
                : t('freeTier.videosUsed', {
                    count: subscription.videos_generated_count,
                    max: subscription.limits.max_videos
                  })}
            </p>
            <Button
              size="sm"
              className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white text-xs w-full sm:w-auto sm:shrink-0"
              onClick={() => navigate('/pricing')}
            >
              {tier === 'starter'
                ? (t('starterTier.upgradeCta') || 'Auf Autopilot upgraden')
                : t('freeTier.upgradeUnlimited')}
            </Button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="relative max-w-7xl mx-auto px-4 py-8">
        {children}
      </main>
      {/* Legal links footer. Required to be reachable from every page
          (DSGVO + § 5 ECG). Renders identically inside AppLayout, on
          legal pages, and on the public landing page. */}
      <Footer />
    </div>
  );
}
